import { QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ErrorBoundary } from '@ui';
import { AdminLayout, AdminPanelIndexPage, CaseLayout, RootLayout } from '@lp/layouts';
import { CaseDetails } from '@lp/pages/CaseDetails';
import { Home } from '@lp/pages/Home';
import { SignIn } from '@lp/pages/SignIn';
import { AuthProvider } from '@lp/core/auth';
import { ProfilePage } from '@lp/pages/ProfilePage';
import { NotificationsHub } from '@lp/pages/NotificationsHub/NotificationsHub';
import { getSchedulerRoutes } from '@lp/core/routing/scheduler.routes';
import { ArchiveCasesList } from '@lp/features/ArchiveCasesList/ArchiveCasesList';
import { BulkAssignmentPage } from '@lp/pages/BulkAssignmentPage/BulkAssignmentPage';
import { NoAccessPage } from '@lp/pages/NoAccessPage/NoAccessPage';
import { MultiCaseSuggest, MultiCaseSuggestResults } from '@lp/pages/MultiCaseSuggest';
import { ChatHub } from '@lp/features/ChatHub/ChatHub';
import {
  EmployeesList,
  EmployeesEdit,
  ContractorsList,
  ContractorsEdit,
  HospitalsList,
  HospitalCreate,
  HospitalEdit,
  HospitalContactsList,
  HospitalContactCreate,
  HospitalContactEdit,
  OPOList,
  OpoCreate,
  OpoEdit,
  HubsList,
  HubCreate,
  HubEdit,
  RegionList,
  RegionEdit,
  TXCentersList,
  TXCentersCreate,
  TXCentersEdit,
  TXUsersList,
  TXUsersCreate,
  TXUsersEdit,
  OcsConsolesList,
  OcsConsolesCreate,
  OcsConsolesEdit,
  PerfusionSetsList,
  LogisticProvidersPartnersList,
  LogisticProvidersTXCenterList,
  LogisticProvidersCreate,
  LogisticProvidersEdit,
  BannedTailNumbersList,
  BannedTailNumbersCreate,
  BannedTailNumbersEdit,
  BannedDriversList,
  BannedDriversCreate,
  BannedDriversEdit,
  ReleaseNotes,
} from '@lp/pages/admin';
import { CaseLogisticsPlanner as CaseLogisticsPlannerPage } from '@lp/pages/CaseLogisticsPlanner';
import { LogisticsChangeLogDrawer } from '@lp/features/RoutePlanner/components/LogisticsChangeLog/LogisticsChangeLogDrawer';
import { DeviceTrackingLogs } from '@lp/features/RoutePlanner/components/DeviceTrackingLog/DeviceTrackingLogs';
import { Settings } from '@lp/features/Settings';
import { NotificationsSettingsPage } from '@lp/features/Settings/NotificationsSettingsPage/NotificationsSettingsPage';
import { routePermissionConfig } from '@lp/core/routing/config';
import { OpoContactsList } from '@lp/pages/admin/opo-contacts/OpoContactList';
import { OpoContactCreate } from '@lp/pages/admin/opo-contacts/OpoContactCreate';
import { OpoContactEdit } from '@lp/pages/admin/opo-contacts/OpoContactEdit';
import { EmployeeType } from '@/api/apis/resources';
import { queryClient } from '@/queries';
import { DocumentTitle } from '@/routing/document-title';
import { profileLoader } from '@/routing/loaders/profile.loader';
import { hospitalByIdLoader, hospitalContactByIdLoader } from '@/routing/loaders/hospitals.loader';
import { opoByIdLoader, opoContactByIdLoader } from '@/routing/loaders/opo.loader';
import { hubByIdLoader } from '@/routing/loaders/hubs.loader';
import { employeeByIdLoader } from '@/routing/loaders/employees.loader';
import { txCenterByIdLoader } from '@/routing/loaders/tx-center.loader';
import { partnerProviderByIdLoader } from '@/routing/loaders/partner-provider.loader';
import { txCenterProviderByIdLoader } from '@/routing/loaders/txcenter-provider.loader';
import { composedTxUserByIdLoader } from '@/routing/loaders/tx-user.loader';
import { ocsConsolesByIdLoader } from '@/routing/loaders/ocs-console.loader';
import { bannedTailNumbersByIdLoader } from '@/routing/loaders/banned-tail-numbers.loader';
import { bannedDriversByIdLoader } from '@/routing/loaders/banned-drivers.loader';
import { regionByIdLoader } from '@/routing/loaders/regions.loader';
import { multiSuggestQueryClient } from '@/queries/multi-suggest/multi-suggest.utils';
import { CaseIdProvider } from '@/contexts';
import { personalizationLoader } from '@/utils/helpers';
import { AuthGuard, AutoGuardedOutlet } from './guards';
import { DEFAULT_TITLE, routeTitleConfig } from './route-title.config';
import { CaseRouteLayout } from './CaseRouteLayout';

export const getRouter = () =>
  createBrowserRouter([
    {
      path: '/',
      element: (
        <AuthProvider>
          <DocumentTitle defaultTitle={DEFAULT_TITLE} config={routeTitleConfig} />
          <AuthGuard>
            <AutoGuardedOutlet permissionsMap={routePermissionConfig} />
          </AuthGuard>
        </AuthProvider>
      ),
      children: [
        {
          path: '/no-access',
          element: <NoAccessPage />,
        },
        {
          element: <RootLayout />,
          children: [
            {
              path: '/',
              element: <CaseLayout />,
              children: [
                {
                  index: true,
                  element: <Home />,
                },
                {
                  path: 'case/:caseId',
                  element: (
                    <CaseIdProvider>
                      <CaseRouteLayout />
                    </CaseIdProvider>
                  ),
                  children: [
                    { index: true, element: <Navigate to="details" replace /> },
                    { path: 'details', element: <CaseDetails /> },
                    { path: 'resources', element: <CaseDetails /> },
                    { path: 'clinical', element: <CaseDetails /> },
                    { path: 'log', element: <CaseDetails /> },
                    { path: 'access', element: <CaseDetails /> },
                    { path: 'location-tracking', element: <CaseDetails /> },
                    {
                      path: 'planner',
                      element: <CaseLogisticsPlannerPage />,
                      errorElement: <ErrorBoundary redirectUrl="" errorMessage="Case details loading failed." />,
                      children: [
                        { path: 'change-log', element: <LogisticsChangeLogDrawer /> },
                        { path: 'device-tracking-log', element: <DeviceTrackingLogs /> },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              path: 'notifications',
              element: <NotificationsHub />,
            },
            {
              path: 'dashboard',
              element: <div>Dashboard Page</div>,
            },
            ...getSchedulerRoutes(),
            {
              path: 'archive',
              element: <ArchiveCasesList />,
            },
            {
              path: 'bulk-assignment',
              element: <BulkAssignmentPage />,
            },
            {
              path: 'multi-case-suggest',
              element: <CaseLayout />,
              children: [
                {
                  index: true,
                  element: <MultiCaseSuggest />,
                },
                {
                  path: ':multiSuggestId',
                  element: (
                    <QueryClientProvider client={multiSuggestQueryClient}>
                      <MultiCaseSuggestResults />
                    </QueryClientProvider>
                  ),
                },
              ],
            },

            {
              path: 'chats',
              element: <ChatHub />,
            },
            {
              path: 'profile',
              children: [
                {
                  index: true,
                  loader: () => profileLoader(queryClient),
                  element: <ProfilePage />,
                  errorElement: <ErrorBoundary errorMessage="Profile loading failed." />,
                },
                {
                  id: 'settings',
                  path: 'settings',
                  element: <Settings />,
                  loader: () => personalizationLoader(queryClient),
                  children: [
                    { index: true, element: <Navigate to="notifications" replace /> },
                    { path: 'notifications', element: <NotificationsSettingsPage /> },
                  ],
                },
              ],
            },
            {
              path: 'admin',
              element: <AdminLayout />,
              children: [
                {
                  index: true,
                  element: <AdminPanelIndexPage />,
                },
                {
                  path: 'users',
                  children: [
                    {
                      index: true,
                      element: <Navigate to="/admin/users/employees" />,
                    },
                    {
                      path: 'employees',
                      children: [
                        {
                          index: true,
                          element: <EmployeesList type={EmployeeType.employee} key={EmployeeType.employee} />,
                          errorElement: <ErrorBoundary errorMessage="Employee List loading failed." />,
                        },
                        {
                          path: ':employeeId',
                          loader: ({ params }) => employeeByIdLoader(queryClient)(params.employeeId as string),
                          element: <EmployeesEdit type={EmployeeType.employee} />,
                        },
                      ],
                    },
                    {
                      path: 'contractors',
                      children: [
                        {
                          index: true,
                          element: <ContractorsList key={EmployeeType.contractor} />,
                          errorElement: <ErrorBoundary errorMessage="Contractors List loading failed." />,
                        },
                        {
                          path: ':employeeId',
                          loader: ({ params }) => employeeByIdLoader(queryClient)(params.employeeId as string),
                          element: <ContractorsEdit />,
                        },
                      ],
                    },
                  ],
                },
                {
                  path: 'hospitals',
                  children: [
                    {
                      index: true,
                      element: <Navigate to="/admin/hospitals/locations" />,
                    },
                    {
                      path: 'locations',
                      children: [
                        {
                          index: true,
                          element: <HospitalsList />,
                          errorElement: <ErrorBoundary errorMessage="Hospitals List loading failed." />,
                        },
                        {
                          path: 'create',
                          element: <HospitalCreate />,
                          errorElement: (
                            <ErrorBoundary
                              errorMessage="Hospital entities loading failed."
                              redirectUrl="/admin/hospitals/locations"
                            />
                          ),
                        },
                        {
                          path: ':locationId',
                          loader: ({ params }) => hospitalByIdLoader(queryClient)(params.locationId as string),
                          element: <HospitalEdit />,
                          errorElement: (
                            <ErrorBoundary
                              errorMessage="Hospital entity loading failed."
                              redirectUrl="/admin/hospitals/locations"
                            />
                          ),
                        },
                      ],
                    },
                    {
                      path: 'contacts',
                      children: [
                        {
                          index: true,
                          element: <HospitalContactsList />,
                          errorElement: <ErrorBoundary errorMessage="Contacts List loading failed." />,
                        },
                        {
                          path: 'create',
                          element: <HospitalContactCreate />,
                          errorElement: (
                            <ErrorBoundary
                              errorMessage="Contacts entities loading failed."
                              redirectUrl="/admin/hospitals/contacts"
                            />
                          ),
                        },
                        {
                          path: ':contactId',
                          loader: ({ params }) => hospitalContactByIdLoader(queryClient)(params.contactId as string),
                          element: <HospitalContactEdit />,
                          errorElement: (
                            <ErrorBoundary
                              errorMessage="Contact entity loading failed."
                              redirectUrl="/admin/hospitals/contacts"
                            />
                          ),
                        },
                      ],
                    },
                  ],
                },
                {
                  path: 'opos',
                  children: [
                    {
                      index: true,
                      element: <Navigate to="/admin/opos/locations" />,
                    },
                    {
                      path: 'locations',
                      children: [
                        {
                          index: true,
                          element: <OPOList />,
                          errorElement: <ErrorBoundary errorMessage="OPO List loading failed." />,
                        },
                        {
                          path: 'create',
                          element: <OpoCreate />,
                          errorElement: (
                            <ErrorBoundary
                              errorMessage="OPO entities loading failed."
                              redirectUrl="/admin/opos/locations"
                            />
                          ),
                        },
                        {
                          path: ':locationId',
                          loader: ({ params }) => opoByIdLoader(queryClient)(params.locationId as string),
                          element: <OpoEdit />,
                          errorElement: (
                            <ErrorBoundary
                              errorMessage="OPO entity loading failed."
                              redirectUrl="/admin/opos/locations"
                            />
                          ),
                        },
                      ],
                    },
                    {
                      path: 'contacts',
                      children: [
                        {
                          index: true,
                          element: <OpoContactsList />,
                          errorElement: <ErrorBoundary errorMessage="Contacts List loading failed." />,
                        },
                        {
                          path: 'create',
                          element: <OpoContactCreate />,
                          errorElement: (
                            <ErrorBoundary
                              errorMessage="OPO Contacts entities loading failed."
                              redirectUrl="/admin/opos/contacts"
                            />
                          ),
                        },
                        {
                          path: ':contactId',
                          loader: ({ params }) => opoContactByIdLoader(queryClient)(params.contactId as string),
                          element: <OpoContactEdit />,
                          errorElement: (
                            <ErrorBoundary
                              errorMessage="Contact entity loading failed."
                              redirectUrl="/admin/opos/contacts"
                            />
                          ),
                        },
                      ],
                    },
                  ],
                },
                {
                  path: 'hubs',
                  children: [
                    {
                      index: true,
                      element: <Navigate to="/admin/hubs/locations" />,
                    },
                    {
                      path: 'locations',
                      element: <HubsList />,
                      errorElement: <ErrorBoundary errorMessage="Hubs List loading failed." />,
                    },
                    {
                      path: 'locations/create',
                      element: <HubCreate />,
                      errorElement: (
                        <ErrorBoundary
                          errorMessage="Hub entities loading failed."
                          redirectUrl="/admin/hubs/locations"
                        />
                      ),
                    },
                    {
                      path: 'locations/:locationId',
                      loader: ({ params }) => hubByIdLoader(queryClient)(params.locationId as string),
                      element: <HubEdit />,
                      errorElement: (
                        <ErrorBoundary errorMessage="Hub entity loading failed." redirectUrl="/admin/hubs/locations" />
                      ),
                    },
                  ],
                },
                {
                  path: 'regions',
                  children: [
                    {
                      index: true,
                      element: <RegionList />,
                    },
                    {
                      path: ':regionId',
                      loader: ({ params }) => regionByIdLoader(queryClient)(params.regionId as string),
                      element: <RegionEdit />,
                      errorElement: (
                        <ErrorBoundary errorMessage="Region entity loading failed." redirectUrl="/admin/regions" />
                      ),
                    },
                  ],
                },
                {
                  path: 'customers',
                  children: [
                    {
                      index: true,
                      element: <Navigate to="admin/customers/tx_centers" />,
                    },
                    {
                      path: 'tx_centers',
                      element: <TXCentersList />,
                      errorElement: <ErrorBoundary errorMessage="TX Centers List loading failed." />,
                    },
                    {
                      path: 'tx_centers/create',
                      element: <TXCentersCreate />,
                      errorElement: (
                        <ErrorBoundary
                          errorMessage="TX Center entities loading failed."
                          redirectUrl="/admin/customers/tx_centers"
                        />
                      ),
                    },
                    {
                      path: 'tx_centers/:txCenterId',
                      loader: ({ params }) => txCenterByIdLoader(queryClient)(params.txCenterId as string),
                      element: <TXCentersEdit />,
                      errorElement: (
                        <ErrorBoundary
                          errorMessage="TX Center entity loading failed."
                          redirectUrl="/admin/customers/tx_centers"
                        />
                      ),
                    },
                  ],
                },
                {
                  path: 'customers',
                  children: [
                    {
                      index: true,
                      element: <Navigate to="admin/customers/tx_users" />,
                    },
                    {
                      path: 'tx_users',
                      element: <TXUsersList />,
                      errorElement: <ErrorBoundary errorMessage="TX Users List loading failed." />,
                    },
                    {
                      path: 'tx_users/create',
                      element: <TXUsersCreate />,
                      errorElement: (
                        <ErrorBoundary
                          errorMessage="TX User entities loading failed."
                          redirectUrl="/admin/customers/tx_users"
                        />
                      ),
                    },
                    {
                      path: 'tx_users/:tx_usersId',
                      loader: ({ params }) => composedTxUserByIdLoader(queryClient)(params.tx_usersId as string),
                      element: <TXUsersEdit />,
                      errorElement: (
                        <ErrorBoundary
                          errorMessage="TX User entity loading failed."
                          redirectUrl="/admin/customers/tx_users"
                        />
                      ),
                    },
                  ],
                },
                {
                  path: 'devices',
                  children: [
                    {
                      index: true,
                      element: <Navigate to="admin/devices/ocs_consoles" />,
                    },
                    {
                      path: 'ocs_consoles',
                      element: <OcsConsolesList />,
                      errorElement: <ErrorBoundary errorMessage="OCS Consoles List loading failed." />,
                    },
                    {
                      path: 'ocs_consoles/create',
                      element: <OcsConsolesCreate />,
                      errorElement: (
                        <ErrorBoundary
                          errorMessage="OCS Console entities loading failed."
                          redirectUrl="/admin/devices/ocs_consoles"
                        />
                      ),
                    },
                    {
                      path: 'ocs_consoles/:consoleId',
                      loader: ({ params }) => ocsConsolesByIdLoader(queryClient)(params.consoleId as string),
                      element: <OcsConsolesEdit />,
                      errorElement: (
                        <ErrorBoundary
                          errorMessage="OCS Console entity loading failed."
                          redirectUrl="/admin/devices/ocs_consoles"
                        />
                      ),
                    },
                    {
                      path: 'ocs_perfusion_sets',
                      element: <PerfusionSetsList />,
                      errorElement: <ErrorBoundary errorMessage="Perfusion Sets List loading failed." />,
                    },
                  ],
                },
                {
                  path: 'logistics_providers',
                  children: [
                    {
                      index: true,
                      element: <Navigate to="admin/logistics_providers" />,
                    },
                    {
                      path: 'supplemental_lift',
                      element: <LogisticProvidersPartnersList />,
                      errorElement: <ErrorBoundary errorMessage="Supplemental Lift Provider List loading failed." />,
                    },
                    {
                      path: 'supplemental_lift/create',
                      element: <LogisticProvidersCreate type="Partner" />,
                      errorElement: (
                        <ErrorBoundary
                          errorMessage="Partner Provider entities loading failed."
                          redirectUrl="/admin/logistics_providers/supplemental_lift"
                        />
                      ),
                    },
                    {
                      path: 'supplemental_lift/:supplementalLiftProviderId',
                      loader: ({ params }) =>
                        partnerProviderByIdLoader(queryClient)(params.supplementalLiftProviderId as string),
                      element: <LogisticProvidersEdit type="Partner" />,
                      errorElement: (
                        <ErrorBoundary
                          errorMessage="Partner Providers entity loading failed."
                          redirectUrl="/admin/logistics_providers/supplemental_lift"
                        />
                      ),
                    },
                    {
                      path: 'tx_center',
                      element: <LogisticProvidersTXCenterList />,
                      errorElement: <ErrorBoundary errorMessage="TX Center Providers List loading failed." />,
                    },
                    {
                      path: 'tx_center/create',
                      element: <LogisticProvidersCreate type="TX Center" />,
                      errorElement: (
                        <ErrorBoundary
                          errorMessage="TX Center Provider entities loading failed."
                          redirectUrl="/admin/logistics_providers/tx_center"
                        />
                      ),
                    },
                    {
                      path: 'tx_center/:providerId',
                      loader: ({ params }) => txCenterProviderByIdLoader(queryClient)(params.providerId as string),
                      element: <LogisticProvidersEdit type="TX Center" />,
                      errorElement: (
                        <ErrorBoundary
                          errorMessage="TX Center Providers entity loading failed."
                          redirectUrl="/admin/logistics_providers/tx_center"
                        />
                      ),
                    },
                  ],
                },
                {
                  path: 'configuration',
                  children: [
                    {
                      index: true,
                      element: <Navigate to="/admin/configuration" />,
                    },
                    {
                      path: 'banned_tail_numbers',
                      element: <BannedTailNumbersList />,
                      errorElement: <ErrorBoundary errorMessage="Banned Tail Numbers List loading failed." />,
                    },
                    {
                      path: 'banned_tail_numbers/create',
                      element: <BannedTailNumbersCreate />,
                      errorElement: (
                        <ErrorBoundary
                          errorMessage="Banned Tail Numbers entities loading failed."
                          redirectUrl="/admin/configuration/banned_tail_numbers"
                        />
                      ),
                    },
                    {
                      path: 'banned_tail_numbers/:bannedTailNumberId',
                      loader: ({ params }) =>
                        bannedTailNumbersByIdLoader(queryClient)(params.bannedTailNumberId as string),
                      element: <BannedTailNumbersEdit />,
                      errorElement: (
                        <ErrorBoundary
                          errorMessage="Banned Tail Numbers entity loading failed."
                          redirectUrl="/admin/configuration/banned_tail_numbers"
                        />
                      ),
                    },
                    {
                      path: 'banned_drivers',
                      element: <BannedDriversList />,
                      errorElement: <ErrorBoundary errorMessage="Banned Drivers List loading failed." />,
                    },
                    {
                      path: 'banned_drivers/create',
                      element: <BannedDriversCreate />,
                      errorElement: (
                        <ErrorBoundary
                          errorMessage="Banned Drivers entities loading failed."
                          redirectUrl="/admin/configuration/banned_drivers"
                        />
                      ),
                    },
                    {
                      path: 'banned_drivers/:bannedDriverId',
                      loader: ({ params }) => bannedDriversByIdLoader(queryClient)(params.bannedDriverId as string),
                      element: <BannedDriversEdit />,
                      errorElement: (
                        <ErrorBoundary
                          errorMessage="Banned Drivers entity loading failed."
                          redirectUrl="/admin/configuration/banned_drivers"
                        />
                      ),
                    },
                  ],
                },
                {
                  path: 'release-notes',
                  element: <ReleaseNotes />,
                  errorElement: <ErrorBoundary errorMessage="Release Notes loading failed." />,
                },
                {
                  path: '*',
                  element: <div>Admin Page</div>,
                },
              ],
            },
          ],
        },
        {
          path: 'sign-in',
          element: <SignIn />,
        },
      ],
    },
  ]);
